/** "Add a field": a standard field not in the list yet, one of the operator's custom fields
 *  (`definitions`, when the host knows them — reused by id, never created twice), or a new
 *  custom field (a name and a type, in a popover; its id is `custom_<slug>`). */
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, ListSubheader, MenuItem, Popover, TextField, Typography } from '@mui/material';
import { useRef, useState } from 'react';

import {
  customFieldClash,
  FIELD_TYPES,
  fieldId,
  STANDARD_FIELDS,
  TYPE_LABEL,
  type CustomFieldDefinition,
  type PreCheckField,
  type PreCheckScope,
} from '../model';

const NEW_CUSTOM = '__custom__';
const DEFINITION = '__definition__:';

export interface AddFieldControlProps {
  scope: PreCheckScope;
  /** The scope's regular fields as they stand. */
  regular: PreCheckField[];
  disabled: boolean;
  onAdd: (next: PreCheckField[], label: string) => void;
  /** The operator's custom fields to offer. Undefined: the host does not know them, and
   *  the menu offers only "Custom field…". */
  definitions?: ReadonlyArray<CustomFieldDefinition>;
}

export function AddFieldControl({ scope, regular, disabled, onAdd, definitions }: AddFieldControlProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [custom, setCustom] = useState({ name: '', type: 'string' });
  const selectRef = useRef<HTMLDivElement>(null);
  const label = scope === 'lead' ? 'the lead booker’s fields' : 'the party members’ fields';
  const present = new Set(regular.map((f) => f.id));
  const standard = STANDARD_FIELDS.filter((f) => !present.has(f.id) && !(scope === 'lead' && f.id === 'is_minor'));
  const offered = (definitions ?? []).filter((d) => !present.has(d.id));
  const customId = fieldId(custom.name);
  const clash = customFieldClash(custom.name, regular, definitions);
  return (
    <>
      <TextField
        ref={selectRef}
        select
        size="small"
        value=""
        label="Add a field"
        disabled={disabled}
        onChange={(e) => {
          const v = e.target.value;
          if (v === NEW_CUSTOM) {
            setAnchor(selectRef.current);
            return;
          }
          if (v.startsWith(DEFINITION)) {
            const d = offered.find((x) => x.id === v.slice(DEFINITION.length));
            if (d) onAdd([...regular, { id: d.id, name: d.name, type: d.type, required: false }], `Add ${d.name} to ${label}`);
            return;
          }
          const f = STANDARD_FIELDS.find((x) => x.id === v);
          if (f) onAdd([...regular, { ...f, required: false }], `Add ${f.name} to ${label}`);
        }}
        sx={{ width: 240 }}
      >
        {standard.map((f) => (
          <MenuItem key={f.id} value={f.id}>
            {f.name}
          </MenuItem>
        ))}
        {offered.length > 0 && <ListSubheader sx={{ lineHeight: '32px' }}>This operator’s custom fields</ListSubheader>}
        {offered.map((d) => (
          <MenuItem key={d.id} value={`${DEFINITION}${d.id}`} sx={{ gap: 1 }}>
            <Box component="span" sx={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {d.name}
            </Box>
            <Typography component="span" variant="caption" color="text.secondary">
              {TYPE_LABEL[d.type] ?? d.type}
            </Typography>
          </MenuItem>
        ))}
        <MenuItem value={NEW_CUSTOM}>{definitions ? 'New custom field…' : 'Custom field…'}</MenuItem>
      </TextField>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 1.5, width: 340, maxWidth: '90vw', display: 'grid', gap: 1 } } }}
      >
        <Typography variant="body2">A custom field belongs to the operator: every tour that asks it shares its name and type.</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            label="Field name"
            value={custom.name}
            error={clash !== null}
            helperText={clash ?? ' '}
            onChange={(e) => setCustom((c) => ({ ...c, name: e.target.value }))}
            inputProps={{ maxLength: 128 }}
            autoFocus
            sx={{ flex: 1 }}
          />
          <TextField select size="small" label="Type" value={custom.type} onChange={(e) => setCustom((c) => ({ ...c, type: e.target.value }))} sx={{ width: 110 }}>
            {FIELD_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button size="small" onClick={() => setAnchor(null)}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            disabled={!custom.name.trim() || clash !== null}
            onClick={() => {
              onAdd([...regular, { id: customId, name: custom.name.trim(), type: custom.type, required: false }], `Add ${custom.name.trim()} to ${label}`);
              setCustom({ name: '', type: 'string' });
              setAnchor(null);
            }}
          >
            Add field
          </Button>
        </Box>
      </Popover>
    </>
  );
}
